import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import { ArrowRight, CheckCircle, Clock, Upload, FileText } from 'lucide-react';

const RightmoveFloorPlanService = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Rightmove Floor Plan Service", path: "/rightmove-floor-plan-service" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        pageId="rightmove-floor-plan-service"
        defaultTitle="Rightmove Floor Plan Service | House Plan Direct"
        defaultDescription="Floor plans formatted for Rightmove listings. Increase buyer engagement with professional floor plans. 24-48 hour delivery from £40. Upload your sketch today."
        aiSummary="House Plan Direct provides Rightmove-ready floor plans for UK property listings. Our floor plans are formatted specifically for Rightmove, Zoopla, OnTheMarket, and other UK property portals. Plans from £40 with 24-48 hour turnaround."
        keywords="Rightmove floor plan, Rightmove floor plan service, floor plan for Rightmove listing, Zoopla floor plan, property portal floor plan UK"
      />
      <SeoBreadcrumbs items={breadcrumbs} />
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Rightmove Floor Plan Service
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Professional floor plans formatted for Rightmove, Zoopla, and UK property portals.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" /> Rightmove Ready
            </span>
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <Clock className="h-4 w-4 text-blue-600 mr-2" /> 24-48 hour delivery
            </span>
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <FileText className="h-4 w-4 text-purple-600 mr-2" /> From £40
            </span>
          </div>
          <Link to="/order">
            <Button size="lg" className="bg-green-600 hover:bg-green-700">
              Order Floor Plan <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="text-sm text-gray-500 mt-4">
            Prefer to do it yourself? Use the free <Link to="/floor-plan-creator" className="text-green-600 underline hover:text-green-700" data-testid="rightmove-link-floor-plan-creator">Floor Plan Creator</Link>.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Floor Plans for Rightmove Listings</h2>
          <p className="text-gray-600 mb-8">
            Rightmove is the UK's largest property portal, and listings with floor plans consistently outperform 
            those without. Our floor plans are specifically formatted for optimal display on Rightmove, Zoopla, 
            OnTheMarket, and other major UK property portals.
          </p>

          <div className="bg-green-50 border border-green-200 rounded-xl p-8 mb-12">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Why Add Floor Plans to Your Rightmove Listing?</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>More Enquiries:</strong> Listings with floor plans receive up to 30% more enquiries</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Better Qualified Buyers:</strong> Buyers understand the layout before viewing</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Professional Presentation:</strong> Stand out from competing listings</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Faster Sales:</strong> Properties with floor plans typically sell faster</span>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-6">Compatible Property Portals</h3>
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            {["Rightmove", "Zoopla", "OnTheMarket", "PrimeLocation"].map((portal, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg text-center">
                <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <span className="font-medium text-gray-900">{portal}</span>
              </div>
            ))}
          </div>

          {/* Pricing */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Pricing</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <div className="text-3xl font-bold text-green-600 mb-2">£40</div>
                <div className="text-gray-600">Up to 3 rooms</div>
              </div>
              <div className="p-4">
                <div className="text-3xl font-bold text-green-600 mb-2">+£5</div>
                <div className="text-gray-600">Per additional room</div>
              </div>
              <div className="p-4">
                <div className="text-3xl font-bold text-green-600 mb-2">24-48hrs</div>
                <div className="text-gray-600">Turnaround</div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-green-600 text-white rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Get Your Rightmove-Ready Floor Plan</h3>
            <p className="text-green-100 mb-6">Upload your sketch and receive a professional floor plan within 24-48 hours.</p>
            <Link to="/order">
              <Button size="lg" variant="secondary" className="bg-white text-green-600 hover:bg-gray-100">
                Order Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RightmoveFloorPlanService;
